/**
 * Sectioned nav model (H §7.1, TMMRSL-0058). Two sections: **Your business** (the reseller's one
 * tenant's nine screens under `/business/*`) then **Reseller** (reseller-wide). A leaf's
 * `ownerOnly` flag hides it from everyone but a reseller OWNER (the GHL integration, H §4.4).
 */
import { HOME_PATH } from '@/routes/home';

export interface NavLeaf {
  kind: 'item';
  id: string;
  /** Material Symbols Outlined glyph name. */
  icon: string;
  /** Catalog key for the visible label. */
  labelKey: string;
  path: string;
  /** Shown to a reseller OWNER only. Presentation — saas-auth still answers 403. */
  ownerOnly: boolean;
}

export interface NavSection {
  id: string;
  labelKey: string;
  items: NavLeaf[];
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'business',
    labelKey: 'nav.sectionBusiness',
    items: [
      {
        kind: 'item',
        id: 'overview',
        icon: 'storefront',
        labelKey: 'tenant.tab.overview',
        path: '/business',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'subscribers',
        icon: 'person',
        labelKey: 'tenant.tab.subscribers',
        path: '/business/subscribers',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'promotions',
        icon: 'local_offer',
        labelKey: 'tenant.tab.promotions',
        path: '/business/promotions',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'affiliates',
        icon: 'campaign',
        labelKey: 'tenant.tab.affiliates',
        path: '/business/affiliates',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'statements',
        icon: 'receipt_long',
        labelKey: 'tenant.tab.statements',
        path: '/business/statements',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'addons',
        icon: 'extension',
        labelKey: 'tenant.tab.addons',
        path: '/business/add-ons',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'people',
        icon: 'badge',
        labelKey: 'tenant.tab.people',
        path: '/business/people',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'domain',
        icon: 'language',
        labelKey: 'tenant.tab.domain',
        path: '/business/domain',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'settings',
        icon: 'settings',
        labelKey: 'tenant.tab.settings',
        path: '/business/settings',
        ownerOnly: false,
      },
    ],
  },
  {
    id: 'reseller',
    labelKey: 'nav.sectionReseller',
    items: [
      {
        kind: 'item',
        id: 'dashboard',
        icon: 'dashboard',
        labelKey: 'nav.dashboard',
        path: HOME_PATH,
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'rateCards',
        icon: 'sell',
        labelKey: 'nav.rateCards',
        path: '/rate-cards',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'commissionPlans',
        icon: 'percent',
        labelKey: 'nav.commissionPlans',
        path: '/commission-plans',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'revenue',
        icon: 'monitoring',
        labelKey: 'nav.revenue',
        path: '/revenue',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'settlements',
        icon: 'payments',
        labelKey: 'nav.settlements',
        path: '/settlements',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'billing',
        icon: 'credit_card',
        labelKey: 'nav.billing',
        path: '/billing',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'agreement',
        icon: 'handshake',
        labelKey: 'nav.agreement',
        path: '/agreement',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'team',
        icon: 'group',
        labelKey: 'nav.team',
        path: '/team',
        ownerOnly: false,
      },
      {
        kind: 'item',
        id: 'integrations',
        icon: 'hub',
        labelKey: 'nav.integrations',
        path: '/integrations/ghl',
        ownerOnly: true,
      },
    ],
  },
];

/** The sections as `isOwner` sees them — owner-only leaves dropped for everyone else. */
export function visibleSections(isOwner: boolean): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((leaf) => isOwner || !leaf.ownerOnly),
  }));
}

/**
 * The leaf whose path is the longest prefix of `pathname`, or null.
 * Overview (`/business`) wins only on exactly `/business` — longer business paths pick their leaf.
 */
export function activeLeaf(pathname: string): NavLeaf | null {
  let best: NavLeaf | null = null;
  for (const section of NAV_SECTIONS) {
    for (const leaf of section.items) {
      if (
        (pathname === leaf.path ||
          (leaf.path !== '/business' && pathname.startsWith(`${leaf.path}/`))) &&
        (best === null || leaf.path.length > best.path.length)
      ) {
        best = leaf;
      }
    }
  }
  return best;
}
